// @ts-nocheck
// Question Loader Service - Duolingo-style pre-translated content
// Loads questions from JSON files (i18n/locales) for instant loading

import type { Language } from '../types_i18n';
import type { Question } from '../types';
import * as questionData from '../question_data';
import { realExamQuestions } from '../question_data/realExamQuestions';
import { logger } from '../utils/logger';
import { offlineStorage } from './offlineStorage';
import { advancedCache } from './advancedCache';
import { sanitizeQuestionsMedia } from '../question_data/animations/animationPlaybackRegistry';

// Map test IDs to question data (CBR 2026 only - Phase B consolidated)
const TEST_QUESTION_MAP: Record<string, () => Question[]> = {
  // CBR 2026 TIER 1
  'traffic-signs': () => [...questionData.mandatorySignsQuestions, ...questionData.prohibitorySignsQuestions, ...questionData.signIdentificationQuestions],
  'road-markings': () => questionData.roadMarkingsQuestions,
  'motorway-rules': () => questionData.motorwayRulesQuestions,
  'vehicle-handling': () => questionData.vehicleHandlingQuestions,
  'tram-interactions': () => questionData.tramInteractionsQuestions,
  'motorway-merging': () => questionData.motorwayRulesQuestions, // backward compat alias
  'risk-awareness': () => questionData.riskAwarenessQuestions,
  'night-visibility': () => questionData.nightVisibilityQuestions,
  // CBR 2026 TIER 2
  'weather-conditions': () => questionData.weatherConditionsQuestions,
  'pedestrian-crossings': () => questionData.pedestrianCrossingsQuestions,
  'construction-zones': () => questionData.constructionZonesQuestions,
  'technology-safety': () => questionData.technologySafetyQuestions,
  'fatigue-rest': () => questionData.fatigueRestQuestions,
  'environmental-zones': () => questionData.environmentalZonesQuestions,
  'incident-first-aid': () => questionData.incidentFirstAidQuestions,
  'vehicle-categories': () => questionData.vehicleCategoriesQuestions,
  'vehicle-documentation': () => questionData.vehicleDocumentationQuestions,
  // CBR 2026 TIER 3 (consolidated)
  'priority-rules': () => questionData.priorityRulesQuestions,
  'mandatory-signs': () => questionData.mandatorySignsQuestions,
  'traffic-lights-signals': () => questionData.trafficLightsSignalsQuestions,
  'prohibitory-signs': () => questionData.prohibitorySignsQuestions,
  'roundabout-rules': () => questionData.roundaboutRulesQuestions,
  'emergency-procedures': () => questionData.emergencyProceduresQuestions,
  'alcohol-drugs': () => questionData.alcoholDrugsQuestions,
  // CBR 2026 Tier 3 (continued)
  'specialized-driving': () => questionData.specializedDrivingQuestions,
  // BONUS
  'adas-safety': () => questionData.adasSafetyQuestions,
  // Backward compat
  'traffic-lights': () => questionData.trafficLightsSignalsQuestions,
  'hazard-perception': () => questionData.riskAwarenessQuestions,
  'speed-limits': () => questionData.speedLimitsQuestions,
  'speed-safety': () => questionData.speedLimitsQuestions,
  'bicycle-interactions': () => questionData.bicycleInteractionsQuestions,
  'road-signs': () => questionData.signIdentificationQuestions,
  'prohibitory-signs-2': () => questionData.prohibitorySignsQuestions,
  'warning-signs': () => [...questionData.mandatorySignsQuestions, ...questionData.prohibitorySignsQuestions, ...questionData.signIdentificationQuestions],
  'road-information': () => questionData.signIdentificationQuestions,
  'parking-rules': () => questionData.vehicleDocumentationQuestions,
  'environmental': () => questionData.environmentalZonesQuestions,
  'insight-practice': () => questionData.priorityRulesQuestions,
  'lane-changing': () => questionData.laneChangingQuestions,
  'overtaking': () => questionData.overtakingQuestions,
};

// Map test IDs to their subject names for matching official CBR questions
const TEST_SUBJECT_MAP: Record<string, string[]> = {
  'traffic-lights': ['Traffic Lights'],
  'traffic-lights-signals': ['Traffic Lights'], // Backward compat
  'priority-rules': ['Priority Rules'],
  'hazard-perception': ['Hazard Perception'],
  'speed-limits': ['Speed Limits'],
  'speed-safety': ['Speed Limits'], // Backward compat
  'bicycle-interactions': ['Bicycle Interactions'],
  'roundabout-rules': ['Roundabout Rules'],
  'tram-interactions': ['Tram Interactions'],
  'pedestrian-crossings': ['Pedestrian Crossings'],
  'construction-zones': ['Construction Zones'],
  'weather-conditions': ['Weather Conditions'],
  'traffic-signs': ['Traffic Signs', 'Warning Signs', 'Prohibitory Signs', 'Mandatory Signs', 'Sign Identification'],
  'road-signs': ['Traffic Signs', 'Warning Signs', 'Prohibitory Signs', 'Mandatory Signs', 'Sign Identification'], // Backward compat
  'mandatory-signs': ['Mandatory Signs'],
  'prohibitory-signs': ['Prohibitory Signs'],
  'prohibitory-signs-2': ['Prohibitory Signs'],
  'warning-signs': ['Warning Signs', 'Mandatory Signs', 'Prohibitory Signs', 'Sign Identification'],
  'road-information': ['Road Information', 'Information Signs'],
  'road-markings': ['Road Markings'],
  'motorway-rules': ['Motorway Rules'],
  'vehicle-categories': ['Vehicle Categories'],
  'vehicle-knowledge': ['Vehicle Knowledge', 'Vehicle Categories'],
  'vehicle-documentation': ['Vehicle Documentation'],
  'parking-rules': ['Parking Rules'],
  'environmental-zones': ['Environmental Zones'],
  'safety-rules': ['Safety Rules'],
  'technology-safety': ['Technology Safety', 'Safety Rules'],
  'adas-safety': ['Safety Rules'], // Backward compat
  'environmental': ['Environmental Zones'],
  'alcohol-drugs': ['Alcohol & Drugs'],
  'fatigue-rest': ['Fatigue & Rest'],
  'emergency-procedures': ['Emergency Procedures'],
  'insight-practice': ['Hazard Perception', 'Priority Rules', 'Speed Limits'], // General practice
  'risk-awareness': ['Risk Awareness'],
  'night-visibility': ['Night', 'Visibility'],
  'incident-first-aid': ['Incident', 'First Aid'],
  'vehicle-handling': ['Vehicle Handling'],
  'lane-changing': ['Lane Changing'],
  'overtaking': ['Overtaking'],
};

/** Minimum questions per run. Ensures meaningful practice sessions and cache quality. */
const MIN_QUESTIONS_PER_RUN = 10;

interface QuestionLoaderOptions {
  useCache?: boolean;
  saveToCache?: boolean;
}

interface PreloadOptions {
  delayMs?: number;
  maxTests?: number;
  skipOnLowResources?: boolean;
}

class QuestionLoaderService {
  /** Strip animationId / imageUrl when there is no playable media in the registry. */
  private finalizeQuestionList(questions: Question[]): Question[] {
    return sanitizeQuestionsMedia(questions);
  }

  /**
   * Load questions with Duolingo-style cache-first strategy
   * 
   * IMPORTANT: This loads ALL questions for the test (the pool).
   * Adaptive filtering/personalization happens AFTER loading.
   * 
   * Flow:
   * 1. Try IndexedDB cache (fastest, works offline)
   * 2. Load from JSON files (fast, pre-translated)
   * 3. Fallback to English questions (always works)
   * 
   * After loading, you can:
   * - Apply adaptive filtering (select subset based on difficulty)
   * - Apply personalization (select based on weak areas)
   * - Use all questions (full test)
   */
  async loadQuestions(
    testId: string,
    language: Language = 'en',
    options: QuestionLoaderOptions = {}
  ): Promise<Question[]> {
    const { useCache = true, saveToCache = true } = options;

    // Step 1: Try advanced cache first (fastest, instant loading)
    if (useCache) {
      const advancedCached = await advancedCache.getCachedQuestions(testId, language);
      if (advancedCached && advancedCached.length > 0) {
        if (advancedCached.length >= MIN_QUESTIONS_PER_RUN) {
          logger.debug(`✅ Loaded ${advancedCached.length} questions from advanced cache for ${testId} (${language})`);
          return this.finalizeQuestionList(advancedCached);
        }
      }
    }

    // Step 2: Try offline storage cache (fallback)
    if (useCache) {
      let cached: Question[] | null = null;
      try {
        cached = await offlineStorage.getQuestions(testId, language);
      } catch (error: any) {
        // Handle database errors gracefully - caching is optional
        logger.warn('Cache read failed (non-critical):', error);
        cached = null;
      }
      
      if (cached && cached.length > 0) {
        // Ensure cached questions also meet minimum requirement
        if (cached.length >= MIN_QUESTIONS_PER_RUN) {
          logger.debug(`✅ Loaded ${cached.length} questions from cache for ${testId} (${language})`);
          // Also cache in advanced cache for next time
          if (saveToCache) {
            advancedCache.cacheQuestions(testId, language, cached).catch(err => {
              logger.error('Error caching questions in advanced cache:', err);
            });
          }
          return this.finalizeQuestionList(cached);
        } else {
          logger.info(`Cached questions for ${testId} (${cached.length}) below minimum (${MIN_QUESTIONS_PER_RUN}), reloading and topping up`);
          // Clear cache and reload to apply top-up
          await offlineStorage.clearCacheForTest(testId);
        }
      }
    }

    // Step 2: Load base questions (English)
    const baseQuestions = this.loadBaseQuestions(testId);
    if (!baseQuestions || baseQuestions.length === 0) {
      logger.error(`No questions found for test: ${testId}`);
      return [];
    }

    // Step 3: Get translated version if needed
    // INSTANT TRANSLATION: Load in correct language immediately (no English flash)
    let questions: Question[];
    if (language === 'en') {
      questions = baseQuestions;
    } else {
      // Try to load multilingual questions directly (instant, no English flash)
      const translated = await this.loadTranslatedQuestions(testId, baseQuestions, language);
      if (translated && translated.length > 0) {
        questions = translated; // Use translated questions immediately
      } else {
        // Only fallback to English if translation completely fails
        questions = baseQuestions;
        logger.warn(`Translation failed for ${testId} (${language}), using English fallback`);
      }
    }

    // Step 4: Ensure minimum questions per run by topping up with official CBR questions
    if (questions.length < MIN_QUESTIONS_PER_RUN) {
      const needed = MIN_QUESTIONS_PER_RUN - questions.length;
      logger.info(`Test ${testId} has only ${questions.length} questions. Topping up with ${needed} official CBR questions.`);
      const topUpQuestions = this.topUpWithOfficialQuestions(testId, questions, needed);
      questions = [...questions, ...topUpQuestions];
      logger.info(`✅ Topped up ${testId} to ${questions.length} questions (added ${topUpQuestions.length} official CBR questions)`);
    }

    // Step 5: Cache for next time
    if (saveToCache && questions.length > 0) {
      // Don't await - cache in background
      offlineStorage.saveQuestions(testId, language, questions).catch((error: any) => {
        // Suppress database deleted errors - they're handled by global error handler
        const errorMessage = String(error?.message || error?.toString() || '').toLowerCase();
        if (!errorMessage.includes('database deleted')) {
          logger.warn('Failed to cache questions (non-critical):', error);
        }
      });
    }

    logger.debug(`✅ Loaded ${questions.length} questions for ${testId} (${language})`);
    return this.finalizeQuestionList(questions);
  }

  /**
   * Load base questions (English) from question_data
   */
  private loadBaseQuestions(testId: string): Question[] {
    const loader = TEST_QUESTION_MAP[testId];
    if (!loader) {
      logger.warn(`Unknown test ID: ${testId}, using default`);
      return questionData.signIdentificationQuestions;
    }

    try {
      return loader();
    } catch (error) {
      logger.error(`Failed to load questions for ${testId}:`, error);
      return [];
    }
  }

  /**
   * Load translated questions from JSON files (i18n/locales)
   * This is the Duolingo-style approach: pre-translated content
   */
  private async loadTranslatedQuestions(
    testId: string,
    baseQuestions: Question[],
    language: Language
  ): Promise<Question[] | null> {
    try {
      const { getTranslatedQuestions, hasTranslations, translateQuestionsById } = await import('./questionTranslationService');

      if (!baseQuestions.length) return null;

      // English: canonical base copy only (sync with multilingual `text.en` in TS pools).
      if (language === 'en') {
        return baseQuestions;
      }

      // 1) Per-test multilingual merge (when this topic is registered in questionTranslationService).
      let merged: Question[] = hasTranslations(testId)
        ? getTranslatedQuestions(testId, baseQuestions, language)
        : baseQuestions;

      // 2) Full-registry pass by question ID (all CBR pools). Maximizes Dutch/Arabic coverage when a
      //    topic bundle omits an ID or tests combine multiple sources (e.g. traffic-signs).
      merged = translateQuestionsById(merged, language);

      logger.debug(`✅ Resolved ${merged.length} questions for ${testId} (${language})`);
      return merged.length > 0 ? merged : null;
    } catch (error) {
      logger.error(`Failed to load translated questions for ${testId} (${language}):`, error);
      return null;
    }
  }

  /**
   * Pre-cache all questions for offline support (Duolingo-style)
   * Call this on app start to cache everything
   */
  async preloadAllQuestions(
    languages: Language[] = ['en', 'nl', 'ar'],
    options: PreloadOptions = {}
  ): Promise<void> {
    const { delayMs = 20, maxTests, skipOnLowResources = true } = options;
    if (skipOnLowResources && this.isLowResourceDevice()) {
      logger.info('Pre-cache skipped: low-resource or data-saver device detected');
      return;
    }

    logger.info('Starting pre-cache of all questions...');
    const testIds = Object.keys(TEST_QUESTION_MAP);
    const targetTestIds = typeof maxTests === 'number' ? testIds.slice(0, maxTests) : testIds;
    let cached = 0;
    let failed = 0;

    for (const language of languages) {
      for (const testId of targetTestIds) {
        try {
          if (this.shouldAbortPreload()) {
            logger.warn('Pre-cache stopped: page is not active');
            return;
          }
          // Load and cache (this will save to IndexedDB)
          await this.loadQuestions(testId, language, { useCache: false, saveToCache: true });
          cached++;
          
          // Small delay to avoid blocking UI
          await new Promise(resolve => setTimeout(resolve, delayMs));
        } catch (error) {
          logger.warn(`Failed to pre-cache ${testId} (${language}):`, error);
          failed++;
        }
      }
    }

    logger.info(`Pre-cache complete: ${cached} cached, ${failed} failed`);
  }

  private isLowResourceDevice(): boolean {
    if (typeof navigator === 'undefined') return false;
    const connection = (navigator as any).connection;
    const saveData = connection?.saveData;
    const deviceMemory = (navigator as any).deviceMemory;
    const cores = navigator.hardwareConcurrency;
    return Boolean(saveData) ||
      (typeof deviceMemory === 'number' && deviceMemory < 4) ||
      (typeof cores === 'number' && cores <= 4);
  }

  private shouldAbortPreload(): boolean {
    if (typeof document === 'undefined') return false;
    return document.visibilityState === 'hidden';
  }

  /**
   * Clear cache for a specific test (useful for updates)
   */
  async clearCache(testId: string): Promise<void> {
    await offlineStorage.clearCacheForTest(testId);
  }

  /**
   * Clear all caches
   */
  async clearAllCaches(): Promise<void> {
    await offlineStorage.clearCache();
  }

  /**
   * Top up questions with official CBR questions to reach minimum count
   * Only uses official CBR questions from realExamQuestions
   */
  private topUpWithOfficialQuestions(
    testId: string,
    existingQuestions: Question[],
    needed: number
  ): Question[] {
    const subjectMatches = TEST_SUBJECT_MAP[testId] || [];
    if (subjectMatches.length === 0) {
      logger.warn(`No subject mapping found for test ${testId}, cannot top up with official questions`);
      return [];
    }

    // Get existing question IDs to avoid duplicates
    const existingIds = new Set(existingQuestions.map(q => q.id));

    // Filter official CBR questions by matching subjects; exclude image questions from top-up
    const matchingOfficialQuestions = realExamQuestions.filter(q => {
      if ((q as any).imageUrl) return false;
      return subjectMatches.some(subject =>
        q.subject === subject ||
        q.subject.includes(subject) ||
        subject.includes(q.subject)
      );
    });

    if (matchingOfficialQuestions.length === 0) {
      logger.warn(`No matching official CBR questions found for test ${testId} with subjects: ${subjectMatches.join(', ')}`);
      return [];
    }

    // Convert RealExamQuestion to Question format and filter out duplicates
    const availableQuestions: Question[] = matchingOfficialQuestions
      .filter(q => !existingIds.has(q.id))
      .map(q => ({
        id: q.id,
        text: q.text,
        options: q.options,
        correctAnswerId: q.correctAnswerId,
        explanation: q.explanation,
        subject: q.subject,
        imageUrl: (q as any).imageUrl, // Preserve imageUrl if present
      }));

    // Shuffle and take needed amount
    const shuffled = [...availableQuestions].sort(() => Math.random() - 0.5);
    const selected = shuffled.slice(0, needed);

    if (selected.length < needed) {
      logger.warn(`Only found ${selected.length} official CBR questions for ${testId}, needed ${needed}`);
    }

    return selected;
  }
}

export const questionLoader = new QuestionLoaderService();
